import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader } from "../components/UI";
import { toast } from "sonner";
import { Trophy, Medal, Award, Loader2, Info } from "lucide-react";

const TIER = (pts) => {
  if (pts >= 250) return { label: "Ambassador", color: "text-fuchsia-300" };
  if (pts >= 100) return { label: "Captain", color: "text-amber-300" };
  if (pts >= 40) return { label: "Regular", color: "text-cyan-300" };
  if (pts >= 10) return { label: "Booster", color: "text-emerald-300" };
  return { label: "Newcomer", color: "text-slate-400" };
};

export default function VolunteerLeaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/political-os/volunteers/leaderboard?limit=100")
      .then(r => setData(r.data))
      .catch(e => toast.error(errMsg(e, "Could not load leaderboard")))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;
  if (!data) return null;

  const rows = data.leaderboard || [];
  const topThree = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="volunteer-leaderboard-page">
      <PageHeader
        eyebrow="Operations · Recognition"
        title="Volunteer Leaderboard"
        subtitle={`${data.total_volunteers || 0} volunteers ranked by points earned across shifts, event check-ins and opposition ad sightings.`}
      />

      {topThree.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="leaderboard-podium">
          {topThree.map((v, i) => {
            const tier = TIER(v.points);
            const rank = i + 1;
            const icons = [
              <Trophy className="w-5 h-5 text-amber-300" key="1" />,
              <Medal className="w-5 h-5 text-slate-300" key="2" />,
              <Award className="w-5 h-5 text-orange-300" key="3" />,
            ];
            return (
              <Card key={v.key} className={`border-2 ${rank === 1 ? "border-amber-400/50" : rank === 2 ? "border-slate-300/40" : "border-orange-400/40"}`}
                    data-testid={`podium-rank-${rank}`}>
                <div className="flex items-center gap-2 mb-3">
                  {icons[i]}
                  <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-medium">#{rank}</div>
                </div>
                <div className="font-display text-xl font-bold text-white truncate">{v.name}</div>
                {v.email && <div className="text-xs text-slate-500 truncate">{v.email}</div>}
                <div className="mt-3 flex items-baseline gap-2">
                  <div className="font-display text-3xl font-bold text-cyan-300">{v.points}</div>
                  <div className="text-xs text-slate-400">pts</div>
                  <div className={`text-xs ml-auto ${tier.color}`}>{tier.label}</div>
                </div>
                <div className="mt-3 pt-3 border-t border-white/5 text-[10px] text-slate-500 grid grid-cols-2 gap-1">
                  <div>Shifts worked: <span className="text-slate-300">{v.shifts_complete}</span></div>
                  <div>Shifts upcoming: <span className="text-slate-300">{v.shifts_upcoming}</span></div>
                  <div>Check-ins: <span className="text-slate-300">{v.checkins}</span></div>
                  <div>Sightings: <span className="text-slate-300">{v.sightings}</span></div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Card padded={false} data-testid="leaderboard-table-card">
        <div className="px-5 py-3 border-b border-white/10 flex items-center gap-2">
          <Trophy className="w-4 h-4 text-cyan-300" />
          <h3 className="font-display text-lg font-bold text-white">Full ranking</h3>
        </div>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="leaderboard-empty">
            No volunteers on the board yet — as folks claim shifts, RSVP to events or log ad sightings, they&apos;ll appear here.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="leaderboard-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
                <th className="px-5 py-3 w-16">Rank</th>
                <th className="px-5 py-3">Volunteer</th>
                <th className="px-5 py-3 text-right">Points</th>
                <th className="px-5 py-3 text-right">Tier</th>
                <th className="px-5 py-3 text-right">Shifts</th>
                <th className="px-5 py-3 text-right">Events</th>
                <th className="px-5 py-3 text-right">Sightings</th>
              </tr>
            </thead>
            <tbody>
              {rest.map((v, i) => {
                const tier = TIER(v.points);
                return (
                  <tr key={v.key} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`leaderboard-row-${i + 4}`}>
                    <td className="px-5 py-3 text-slate-500">#{i + 4}</td>
                    <td className="px-5 py-3">
                      <div className="text-slate-200">{v.name}</div>
                      {v.email && <div className="text-[10px] text-slate-500">{v.email}</div>}
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-cyan-300 font-semibold">{v.points}</td>
                    <td className={`px-5 py-3 text-right text-xs ${tier.color}`}>{tier.label}</td>
                    <td className="px-5 py-3 text-right text-xs text-slate-400">{v.shifts_complete + v.shifts_upcoming}</td>
                    <td className="px-5 py-3 text-right text-xs text-slate-400">{v.rsvps} · {v.checkins}✓</td>
                    <td className="px-5 py-3 text-right text-xs text-slate-400">{v.sightings}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <Card data-testid="leaderboard-rules-card">
        <div className="flex items-start gap-3">
          <Info className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" />
          <div className="text-xs text-slate-400 leading-relaxed">
            <div className="text-white font-semibold text-sm mb-2">How points are earned</div>
            <ul className="space-y-1 pl-4 list-disc">
              <li><span className="text-cyan-300 font-semibold">{data.rules?.shift_upcoming || 5} pts</span> — sign up for a volunteer shift</li>
              <li><span className="text-cyan-300 font-semibold">{data.rules?.shift_complete || 10} pts</span> — shift completed (end time passed)</li>
              <li><span className="text-cyan-300 font-semibold">{data.rules?.rsvp || 3} pts</span> — RSVP to a public event</li>
              <li><span className="text-cyan-300 font-semibold">{data.rules?.rsvp_checkin || 5} pts</span> — checked in at the event</li>
              <li><span className="text-cyan-300 font-semibold">{data.rules?.ad_sighting || 5} pts</span> — logged an opposition ad sighting via the volunteer portal</li>
            </ul>
            <div className="mt-2 text-slate-500">Points award automatically from existing activity — no separate check-in required.</div>
          </div>
        </div>
      </Card>
    </div>
  );
}
